'use client';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { trackCustomEvent } from '@/lib/analytics';
import { waitForGoogletagLoaded } from './helpers';
import HouseBanner, { isOnScreen } from './HouseBanner';

interface TopPageAdProps {
	index: number;
  identifier: string;
}

const TopPageAd: React.FC<TopPageAdProps> = ({ index, identifier }) => {
	const cardElementRef = useRef<HTMLDivElement | null>(null);
	const { ref: inViewRef, inView } = useInView({
		triggerOnce: false,
		threshold: 0.1,
	});
	// Eager trigger: request the ad ~1200px before the slot is visible so
	// GPT's no-fill verdict lands while the slot is still off screen and
	// the collapse is invisible (see AdComponent's design note).
	const { ref: eagerRef, inView: nearView } = useInView({
		triggerOnce: false,
		threshold: 0,
		rootMargin: '1200px 0px',
	});
	const [gaEvent, setGaEvent] = useState(false);
	// No-fill: collapse immediately. With eager requests this is almost
	// always off screen; for slots visible at page load it's one small
	// shift ~1s in — better than parking a permanent white band at the
	// top of the page (the hold-until-scroll-out variant was rejected in
	// local testing for exactly that).
	const [collapsed, setCollapsed] = useState(false);
	// On-screen no-fill keeps the box and shows our own banner instead of
	// collapsing (see HouseBanner.tsx); off-screen slots still collapse.
	const [houseBanner, setHouseBanner] = useState(false);
	const requestCollapse = () => {
		if (isOnScreen(cardElementRef.current)) setHouseBanner(true);
		else setCollapsed(true);
	};
	const adShownRef = useRef(false);
	const [slotDefined, setSlotDefined] = useState(false);
	const reactId = useId();
	// Hydration-safe unique suffix to prevent slot-id clashes on client-side navigation.
	const [uniqueId, setUniqueId] = useState('');
	useEffect(() => {
		setUniqueId(Math.random().toString(36).substring(2, 8));
	}, []);
	const adDivId = useMemo(() => {
		const sanitizedReactId = reactId.replace(/[^a-zA-Z0-9_-]/g, '');
		const sanitizedIdentifier = identifier.replace(/[^a-zA-Z0-9_-]/g, '-');
		const suffix = uniqueId ? `-${uniqueId}` : '';
		return `div-gpt-ad-dweb-topVideo-${sanitizedIdentifier}-${index}-${sanitizedReactId}${suffix}`;
	}, [identifier, index, reactId, uniqueId]);
	const targetSlotRef = useRef<any>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Initialize ad slot
	useEffect(() => {
		if (!uniqueId) return;
		let isMounted = true;

		waitForGoogletagLoaded().then(() => {
			if (!isMounted || !window.googletag) return;

			window.googletag.cmd.push(() => {
				if (!isMounted) return;
				if (!window.isGptInitialized) {
					window.googletag.pubads().disableInitialLoad();
					window.googletag.enableServices();
					// @ts-ignore
					window.isGptInitialized = true;
				}
				const existingSlots = window.googletag.pubads().getSlots();
				const existing = existingSlots.find(
					(s:any) => s.getSlotElementId() === adDivId
				);
				if (existing) {
					window.googletag.destroySlots([existing]);
				}

				// Keep defineSlot sizes aligned with mapping so GPT can serve valid creative sizes.
				const sizeMapping = window.googletag.sizeMapping()
					.addSize([1280, 0], [[728, 90]]) // xl:w-3/5 (>=1280px)
					.addSize([1024, 0], [[728, 90]]) // lg:w-3/5 (>=1024px)
					.addSize([768, 0], [[728, 90], [320, 50]]) // md:w-full (>=768px)
					.addSize([0, 0], [[320, 50], [1, 1]]) // mobile
					.build();

				targetSlotRef.current = window.googletag
					.defineSlot(`/22099787619/${identifier}`, [[728, 90], [320, 50], [1, 1]], adDivId)
					.defineSizeMapping(sizeMapping)
					.addService(window.googletag.pubads());

				// No-fill: requestCollapse() (see above) — never yanks
				// visible content, but releases the reserved band once the
				// slot leaves the viewport instead of holding whitespace
				// forever.
				const onRenderEnded = (event: any) => {
					if (event.slot.getSlotElementId() === adDivId && event.isEmpty) {
						requestCollapse();
					}
				};
				window.googletag.pubads().addEventListener('slotRenderEnded', onRenderEnded);
				targetSlotRef.current._cleanupRenderListener = () => {
					window.googletag.pubads().removeEventListener('slotRenderEnded', onRenderEnded);
				};

				setSlotDefined(true);
			});
		}).catch((e) => {
			console.error(e);
			// googletag never loaded (ad blocker, script failure) — no ad is
			// ever coming, so treat it like a no-fill.
			if (isMounted) requestCollapse();
		});

		return () => {
			isMounted = false;

			if (timeoutRef.current) clearTimeout(timeoutRef.current);

			targetSlotRef.current?._cleanupRenderListener?.();

			window.googletag?.cmd.push(() => {
				const slots = window.googletag.pubads().getSlots();
				const slot = slots.find((s:any) => s.getSlotElementId() === adDivId);
				if (slot) {
					window.googletag.destroySlots([slot]);
				}
			});
		};
	}, [adDivId, uniqueId]);

	// Handle ad display when approaching the viewport (eager, ~1200px out)
	useEffect(() => {
		if (nearView && !adShownRef.current && slotDefined && targetSlotRef.current) {
			// Lock before issuing GPT commands to avoid duplicate display/refresh races.
			adShownRef.current = true;
			window.googletag?.cmd.push(function () {
				window.googletag.display(adDivId);
				window.googletag.pubads().refresh([targetSlotRef.current]);
			});
		}
	}, [nearView, slotDefined, adDivId]);

	// Handle analytics tracking
	useEffect(() => {
		if (inView && adShownRef.current && !gaEvent) {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => {
				setGaEvent(true);
				trackCustomEvent('DWEB_AD_VIEW', {
					category: "DWEB_AD_VIEW",
					action: "pageview",
					label: cardElementRef.current?.id || '',
					customData: { position: index },
				});
			}, 500);
		}
	}, [inView, gaEvent, index]);

	if (collapsed) {
		// With eager requests, no-fill verdicts almost always arrive while
		// the slot is still off screen, so this unmount is usually invisible.
		return null;
	}

	if (houseBanner) {
		return (
			<div
				className='flex justify-center items-center max-[727px]:hidden'
				style={{ minHeight: 90, margin: '16px 0px' }}
			>
				<HouseBanner variant='strip' />
			</div>
		);
	}

	return (
		<div
			ref={(el) => {
				cardElementRef.current = el;
				inViewRef(el);
				eagerRef(el);
			}}
			id={adDivId}
			// Hidden below 728px with CSS rather than a JS width check: the
			// JS version rendered the 90px band in the server HTML and removed
			// it after hydration on phones, shifting the page up.
			className='flex justify-center items-center max-[727px]:hidden'
			style={{
				// Reserve the slot's dominant creative height (728x90 on the
				// >=768px breakpoints where this component is visible at all —
				// it force-hides below 728px viewports). Without this, GPT's
				// late-injected banner pushed the entire page down, and this
				// slot sits directly above the video card / LCP element on
				// video, city, and district pages (Aug 2026 Lighthouse audit).
				// minWidth stays unset: height is what shifts, and a forced
				// 728px width could overflow just-above-threshold viewports.
				minHeight: 90,
				textAlign: 'center',
				margin: '16px 0px',
			}}
		/>
	);
};

export default TopPageAd;